import { useAtomValue } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import Button from '@gds/Button/Button';
import { Input } from '@gds/Input/Input';
import Slider from '@gds/Slider/Slider';
import { Typography } from '@gds/Typography/Typography';
import { useRangeFilters } from '@search-filters/Hooks/UseRangeFilters';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import { TEST_RANGE } from './YearFilter.constants';

import piStyles from '../PairedInputs/PairedInputs.module.css';
import styles from './YearFilter.module.css';

type YearFilterProps = FilterControlBaseProps;

export const YearFilter = ({ strings, tenant }: YearFilterProps) => {
  const minAvailable = TEST_RANGE[0].toString();
  const maxAvailable = TEST_RANGE[TEST_RANGE.length - 1].toString();
  const { totalCount } = useAtomValue(aggregationsAtom);

  const { gteInputProps, lteInputProps, sliderProps, canReset, reset } = useRangeFilters(
    'year',
    TEST_RANGE,
    [minAvailable, maxAvailable],
  );

  return (
    <section className={styles.yearFilter}>
      <Typography className={styles.infoLine} variant="subheading4">
        {strings.yearInfoLine}
      </Typography>
      <div className={piStyles.pairedInputs}>
        <Input
          {...gteInputProps}
          label={strings.min}
          type="number"
          min={minAvailable}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
        <Input
          {...lteInputProps}
          label={strings.max}
          type="number"
          max={maxAvailable}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({totalCount.toLocaleString(ukLocale)})
        </Typography>
        <Button
          color="secondary"
          disabled={!canReset(['stock-condition'])}
          onClick={() => reset(['stock-condition'])}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} className={styles.priceRangeSlider} dataTestId="year-slider" />
    </section>
  );
};
